import { createFileRoute, useNavigate, redirect, Link } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { toast } from '../utils/toast';
import { extractFieldErrors, type FieldErrors } from '../utils/apiError';
import { FiArrowLeft } from 'react-icons/fi';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { ServiceForm, type ServiceFormValues } from '../components/services/ServiceForm';
import {
  useGetServiceDetail,
  useGetTags,
  useUpdateService,
  useDeleteService,
} from '../api/services';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { LoadingLogo } from '../components/common/LoadingLogo';
import { UnauthorizedPage } from '../components/common/UnauthorizedPage';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { UnsavedChangesModal } from '../components/common/UnsavedChangesModal';
import { normalizeServicePriceValue } from '../utils/price';

export const Route = createFileRoute('/services/$serviceId/edit')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: EditServicePage,
});

function getCurrentUser() {
  const userData = localStorage.getItem('user_data');
  if (userData) {
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }
  return null;
}

function EditServicePage() {
  const navigate = useNavigate();
  const { serviceId } = Route.useParams();
  const [isUploading, setIsUploading] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { isConfirmOpen, requestExit, confirmExit, cancelExit, allowNavigation } =
    useUnsavedChangesGuard({ isDirty: isFormDirty });

  const { data: service, isLoading: isLoadingService } = useGetServiceDetail(serviceId);
  // Fetch all tags so we can resolve tag names → IDs for prefill
  const { data: allTags = [], isLoading: isLoadingTags } = useGetTags('');
  const updateService = useUpdateService();
  const deleteService = useDeleteService();

  const userData = getCurrentUser();
  const currentUserId = userData?.id ?? localStorage.getItem('user_id') ?? '';
  const currentUserEmail = userData?.email ?? '';

  const serviceAny = service as any;
  const creatorId = String(serviceAny?.creator?.id ?? serviceAny?.user ?? '');
  const creatorEmail = String(serviceAny?.creator?.email ?? '');

  const isOwner =
    !!currentUserId &&
    !!creatorId &&
    (creatorId === String(currentUserId) ||
      (!!currentUserEmail && currentUserEmail.toLowerCase() === creatorEmail.toLowerCase()));

  const resolvedTagItems = useMemo(() => {
    if (!service || !allTags.length) return [];

    const tagsById = new Map(allTags.map((tag) => [tag.id, tag]));
    const tagsByName = new Map(allTags.map((tag) => [tag.name.trim().toLowerCase(), tag]));
    const resolved = new Map<number, (typeof allTags)[number]>();

    for (const selectedTag of service.user_tags) {
      const byId = selectedTag.id ? tagsById.get(selectedTag.id) : undefined;
      const byName = tagsByName.get(selectedTag.name.trim().toLowerCase());
      const matched = byId ?? byName;
      if (matched) resolved.set(matched.id, matched);
    }

    return Array.from(resolved.values());
  }, [service, allTags]);

  if (isLoadingService || isLoadingTags) {
    return <LoadingLogo />;
  }

  if (!service || !isOwner) {
    return <UnauthorizedPage resourceType="service" resourceId={serviceId} />;
  }

  const handleSubmit = async (values: ServiceFormValues) => {
    setIsUploading(true);
    setServerErrors({}); // Clear previous server errors

    // Upload images in user-defined order, preserving drag-sorted sequence
    let finalImages: string[];
    try {
      finalImages = await Promise.all(
        values.orderedImageEntries.map(async (entry) => {
          if (entry.type === 'existing') return entry.url;
          const [result] = await uploadFilesToCloudinary([entry.file], {
            folder: 'service_images',
            uploadPreset: 'service_preset',
          });
          return result.secure_url;
        }),
      );
    } catch {
      toast.error('Image upload failed. Please try again.');
      setIsUploading(false);
      return;
    }

    try {
      const normalizedPrice = normalizeServicePriceValue(values.price);
      await updateService.mutateAsync({
        serviceId: Number(serviceId),
        title: values.title,
        description: values.description,
        price: normalizedPrice || undefined,
        newImageUrls: [],
        existingImageUrls: finalImages,
        work_example_links: values.workExampleLinks.filter(Boolean),
        user_types: values.userTypeIds,
        tags: values.tagIds,
        alternate_email: values.alternateEmail || undefined,
      });
      allowNavigation();
      navigate({ to: '/services/$serviceId', params: { serviceId }, replace: true });
    } catch (err) {
      // Extract field-specific errors from API response
      const fieldErrors = extractFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        setServerErrors(fieldErrors);
      }
      // Error toast is already shown by the mutation's onError handler
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteService.mutateAsync(Number(serviceId));
      navigate({ to: '/services' });
    } catch {
      // Handled by mutation
    }
  };

  // ── Loading ──────────────────────────────────────────────────────────────
  if (isLoadingService || isLoadingTags) {
    return <LoadingLogo />;
  }

  // ── Not found ────────────────────────────────────────────────────────────
  if (!service) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center gap-4 px-6">
        <p className="text-zinc-400 text-lg">Service not found.</p>
        <Link to="/services" className="text-blue-400 hover:underline text-sm">
          Back to Services
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <main className="pt-20 md:pt-25">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
          <button
            onClick={() =>
              requestExit(() => navigate({ to: '/services/$serviceId', params: { serviceId } }))
            }
            className="p-2 hover:bg-white/5 rounded-full transition-colors group"
          >
            <FiArrowLeft
              size={24}
              className="text-zinc-400 group-hover:text-white transition-colors"
            />
          </button>
        </div>

        <ServiceForm
          title="Edit Service"
          submitLabel="Update Service"
          showDelete
          onSubmit={handleSubmit}
          onDelete={handleDelete}
          isLoading={isUploading || updateService.isPending || deleteService.isPending}
          serverErrors={serverErrors}
          onDirtyChange={setIsFormDirty}
          initialData={{
            title: service.title,
            description: service.description ?? '',
            price: service.price ?? '',
            existingImageUrls: service.images,
            workExampleLinks: service.work_example_links,
            userTypeIds: service.user_types.map((t) => t.id),
            userTypeItems: service.user_types,
            tagIds: resolvedTagItems.map((t) => t.id),
            tagItems: resolvedTagItems,
            alternateEmail: service.alternate_email ?? '',
          }}
        />
      </main>

      <BottomNav />

      {isConfirmOpen && <UnsavedChangesModal onConfirm={confirmExit} onCancel={cancelExit} />}
    </div>
  );
}
